import { Configuration } from '~/core/configuration/configuration.type';
import handleError from '~/utils/errors/handleError';

export type ActionName = 'afterResultsUpdate' | 'beforeProductRender' | 'addToCart' | 'instantSearchInputBackdrop';
export type FilterName = 'filterFulltextResults' | 'beforeProductRender';
export type HookName = ActionName | FilterName;

export type ActionHook = (args?: Record<string, unknown>, isDebugMode?: boolean) => void; // todo: use generics to type args
export type FilterHook<I, T> = <Input extends I, Return extends T>(
    filteredValue: Input,
    args?: Record<string, unknown>,
    isDebugMode?: boolean
) => Return;

export type Hook = ActionHook | FilterHook<unknown, unknown>;

export type Actions = { [key in ActionName]?: ActionHook };
export type Filters = { [key in FilterName]?: FilterHook<unknown, unknown> };
export type Hooks = { [key in HookName]?: Hook };

export interface ISerpPluginBase {
    applyFilter<Input, Return>(
        filterName: FilterName,
        filteredValue: Input,
        args?: Record<string, unknown>,
        isDebugMode?: boolean
    ): Input | Return;
    doAction(actionName: ActionName, args?: Record<string, unknown>, isDebugMode?: boolean): void;
    getHooks(): Hooks;
    getFilters(): Filters;
    setFilters(filters: Filters): void;
    getActions(): Actions;
    setActions(actions: Actions): void;
    beforeSerpRender(config: Configuration): void;
}

export interface ISerpPlugin extends ISerpPluginBase {
    loadActions(): void;
    loadFilters(): void;
    name: string;
}

export type PluginAPI = Pick<ISerpPluginBase, 'applyFilter' | 'doAction' | 'getActions' | 'getFilters'> & {
    name: string;
};

class SerpPluginBase implements ISerpPluginBase {
    protected _filters: Filters;
    protected _actions: Actions;

    constructor() {
        this._actions = {};
        this._filters = {};
    }

    applyFilter<Input, Return>(
        filterName: FilterName,
        filteredValue: Input,
        args?: Record<string, unknown>,
        isDebugMode?: boolean
    ): Input | Return {
        if (!Object.prototype.hasOwnProperty.call(this._filters, filterName) || !this._filters[filterName]) {
            return filteredValue;
        }

        const filter = this._filters[filterName];

        if (typeof filter !== 'function') {
            return filteredValue;
        }

        try {
            return filter(filteredValue, args, isDebugMode);
        } catch (err) {
            handleError(err, "SERP filter '" + filterName + "' failed:", isDebugMode);
            return filteredValue;
        }
    }

    doAction(actionName: ActionName, args?: Record<string, unknown>, isDebugMode?: boolean): void {
        if (!Object.prototype.hasOwnProperty.call(this._actions, actionName) || !this._actions[actionName]) {
            return;
        }
        const action = this._actions[actionName];

        try {
            (action as ActionHook)(args, isDebugMode);
        } catch (err) {
            handleError(err, "SERP action '" + actionName + "' failed:", isDebugMode);
        }
    }

    getHooks(): Hooks {
        return { ...this._filters, ...this._actions };
    }

    getActions(): Actions {
        return this._actions;
    }

    getFilters(): Filters {
        return this._filters;
    }

    setActions(actions: Actions): void {
        this._actions = { ...this._actions, ...actions };
    }

    setFilters(filters: Filters): void {
        this._filters = { ...this._filters, ...filters };
    }
    beforeSerpRender(config: Configuration): void {
        //do nothing
    }
}

export default SerpPluginBase;
